import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { SnackbarContext } from '../context/SnackbarContext';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '@mui/material/styles';
import { FixedSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { trackAffiliateClick, trackSearch } from '../analytics';
import Breadcrumb from './Breadcrumb';
import ProductCard from './ProductCard';
import { 
  Box, 
  Typography, 
  TextField,
  MenuItem 
} from '@mui/material';


const ProductList = () => {
  const { showMessage } = useContext(SnackbarContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const theme = useTheme();

  


  // Generate JSON-LD structured data for product list
  const generateProductListJsonLd = (products) => {
    if (!products || products.length === 0) return null;
    
    const jsonLd = {
      "@context": "https://schema.org/",
      "@type": "ItemList",
      "name": "Bästa deals och erbjudanden",
      "description": "Upptäck de bästa deals och erbjudandena online på hittadeals.com",
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 20).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.name,
          "image": product.image,
          "description": product.description || `Köp ${product.name} till bästa pris på hittadeals.com`,
          "sku": product.asin || product.id,
          "url": `${window.location.origin}/produkt/${product.asin || product.id}`,
          "offers": {
            "@type": "Offer",
            "url": product.affiliateUrl || product.amazonUrl,
            "priceCurrency": "SEK",
            "price": product.price,
            "availability": "https://schema.org/InStock"
          }
        }
      }))
    };

    return JSON.stringify(jsonLd);
  };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      let apiUrl;
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
        apiUrl = "http://localhost:5000/api/products";
      } else {
        apiUrl = window.location.origin + "/api/products";
      }
      const response = await axios.get(apiUrl);
      console.log('API response:', response.data);
      
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      
      let errorMessage = 'Kunde inte hämta produkter';
      if (error.response) {
        errorMessage = error.response.data?.error || 
          `Serverfel (${error.response.status})`;
      } else if (error.request) {
        errorMessage = 'Ingen kontakt med servern';
      } else {
        errorMessage = `Nätverksfel: ${error.message}`;
      }
      
      showMessage({
        text: errorMessage,
        variant: 'error',
      });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    
    const debouncedFetch = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (isMounted) fetchProducts();
      }, 300);
    };

    debouncedFetch();

    // Periodisk uppdatering var 30:e minut
    const interval = setInterval(debouncedFetch, 30 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchProducts]);

  const [category, setCategory] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const categories = Array.from(new Set((products || []).map(p => (p.category && p.category.trim() ? p.category.trim() : null)).filter(Boolean)));
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !category || (product.category && product.category.trim() === category);
    return matchesSearch && matchesCategory;
  });
  // Sortera produkterna baserat på sortOrder
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortOrder === "cheapest") {
      return a.price - b.price;
    } else if (sortOrder === "expensive") {
      return b.price - a.price;
    } else if (sortOrder === "discount") {
      // Sortera på rabattprocent (discount), produkter utan rabatt sist
      const discountA = typeof a.discount === 'number' && a.discount > 0 ? a.discount : -1;
      const discountB = typeof b.discount === 'number' && b.discount > 0 ? b.discount : -1;
      return discountB - discountA;
    } else if (sortOrder === "oldest") {
      // "oldest" - äldst först
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateA - dateB;
    } else {
      // "latest" - senaste först
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    }
  });
  const visibleProducts = sortedProducts;



  

  const handleSearchChange = (event) => {
    const searchValue = event.target.value;
    setSearch(searchValue);
    
    // Spåra sökningar (endast när användaren skriver minst 3 tecken)
    if (searchValue.length >= 3) {
      trackSearch(searchValue);
    }
  };

  // Remove: const handleCardClick = (product) => {
  //   navigate(`/product/${product.asin}`);
  // };

  const pageTitle = search ? `Sökresultat för "${search}" - hittadeals.com` : 'Hitta bästa deals och erbjudanden - hittadeals.com';
  const pageDescription = search 
    ? `Hitta bästa deals för "${search}". Jämför priser och spara pengar på hittadeals.com` 
    : 'Upptäck de bästa deals och erbjudandena online. Jämför priser, hitta rabatter och spara pengar på alla dina köp. Uppdateras dagligen med nya fynd!';



  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="/images/hittadeals-social-share.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="HittaDeals" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="/images/hittadeals-social-share.jpg" />
        <link rel="canonical" href={window.location.origin} />

        {generateProductListJsonLd(visibleProducts) && (
          <script type="application/ld+json">
            {generateProductListJsonLd(visibleProducts)}
          </script>
        )}
      </Helmet>

      <Box 
        className="scroll-container"
        sx={{
          padding: 2,
          '& .product-card': {
            marginBottom: 2,
          },
        }}
      >
        {search && (
          <Breadcrumb
            customItems={[
              { label: 'Hem', href: '/' },
            { label: `Sökresultat för "${search}"`, isLast: true }
          ]}
        />
      )}
      
      <Box>
      <Box sx={{
        display: 'flex',
        gap: { xs: 1, sm: 2 },
        mb: { xs: 1.5, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        position: 'sticky',
        top: { xs: '56px', sm: '64px' },
        zIndex: 1100,
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(90deg, #1e1e1e 0%, #2d2d2d 100%)'
          : 'linear-gradient(90deg, #f5faff 0%, #e3f2fd 100%)',
        py: { xs: 1, sm: 2 },
        px: { xs: 1, sm: 2, md: 3, lg: 4 },
        boxShadow: 6,
        borderRadius: 2,
        border: theme.palette.mode === 'dark' 
          ? '2px solid #424242'
          : '2px solid #90caf9',
        mx: { xs: 0, sm: 2 }
      }}>
        <TextField
          label="Sök produkter"
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          sx={{ 
            flex: 1,
            '& .MuiInputBase-root': {
              height: { xs: '40px', sm: '56px' }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        />
        <TextField
          select
          label="Kategori"
          value={category}
          onChange={e => setCategory(e.target.value)}
          sx={{ 
            minWidth: { xs: 120, sm: 180 },
            '& .MuiInputBase-root': {
              height: { xs: '40px', sm: '56px' }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        >
          <MenuItem value="">Alla kategorier</MenuItem>
          {categories.map(cat => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sortera"
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          sx={{ 
            minWidth: { xs: 120, sm: 180 },
            '& .MuiInputBase-root': {
              height: { xs: '40px', sm: '56px' }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        >
          <MenuItem value="latest">Senaste</MenuItem>
          <MenuItem value="oldest">Äldst</MenuItem>
          <MenuItem value="cheapest">Billigast</MenuItem>
          <MenuItem value="expensive">Dyrast</MenuItem>
          <MenuItem value="discount">Störst rabatt</MenuItem>
        </TextField>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <span>Laddar produkter...</span>
        </Box>
      ) : visibleProducts.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <Typography variant="h6">Inga produkter hittades.</Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            width: '100%',
            maxWidth: '100%',
            px: { xs: 0.5, sm: 2, md: 3, lg: 4 },
            height: 'calc(100vh - 250px)', // Höjd för virtuell scrollning
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            overflowX: 'hidden'
          }}>
          <AutoSizer>
            {({ height, width }) => {
              // Beräkna antal kolumner baserat på skärmbredd
              const columnCount = width < 600 ? 2 : // xs
                               width < 960 ? 3 : // sm
                               4; // md och större
              
              // Beräkna produktkortets bredd baserat på antal kolumner och mellanrum
              const columnWidth = width / columnCount;
              
              // Beräkna produktkortets höjd (fast höjd för enkelhetens skull)
              const rowHeight = 450; // Justera detta värde baserat på dina produktkort
              
              // Beräkna antal rader baserat på antal produkter och kolumner
              const rowCount = Math.ceil(visibleProducts.length / columnCount);
              
              return (
                <FixedSizeGrid
                  columnCount={columnCount}
                  columnWidth={columnWidth}
                  height={height}
                  rowCount={rowCount}
                  rowHeight={rowHeight}
                  width={width}
                  itemData={{
                    products: visibleProducts,
                    columnCount,
                    trackAffiliateClick
                  }}
                >
                  {({ columnIndex, rowIndex, style, data }) => {
                    const index = rowIndex * data.columnCount + columnIndex;
                    if (index >= data.products.length) {
                      return null; // Returnera null för tomma celler
                    }
                    const product = data.products[index];
                    return (
                      <div style={style}>
                        <ProductCard 
                          key={product.asin} 
                          product={product} 
                          trackAffiliateClick={data.trackAffiliateClick} 
                          style={{ height: 'auto' }} // Anpassa stil för virtuell scrollning
                        />
                      </div>
                    );
                  }}
                </FixedSizeGrid>
              );
            }}
          </AutoSizer>
        </Box>
      )}
    </Box>
    </Box>
    </>
  );
};



export default ProductList;
